import React, { useEffect, useState, useCallback } from "react";
import "./App.css";
import { Toaster, toast } from "sonner";
import { Menu, Globe, Plug, X, Github, Slack, Cloud, HardDrive, Clock, Play, Trash2, Loader2, Code2, Bot, Users, LogOut, Sparkles, LifeBuoy } from "lucide-react";
import api, { downloadBase64 } from "./lib/api";
import Sidebar from "./components/Sidebar";
import Composer from "./components/Composer";
import MessageList from "./components/MessageList";
import DataStudio from "./components/DataStudio";
import DeepResearch from "./components/DeepResearch";
import DeveloperPanel from "./components/DeveloperPanel";
import AgentPanel from "./components/AgentPanel";
import MembersModal from "./components/MembersModal";
import PricingModal from "./components/PricingModal";
import SelfAIModal from "./components/SelfAIModal";
import AuthScreen from "./components/AuthScreen";
import { AuthProvider, useAuth } from "./lib/AuthContext";
import { Logo } from "./components/Logo";

const uid = () => Math.random().toString(36).slice(2);

function ConnectorsModal({ open, onClose }) {
  if (!open) return null;
  const items = [
    { name: "GitHub", icon: Github, desc: "Repos, issues, code — read/write" },
    { name: "Slack", icon: Slack, desc: "Channels & messages" },
    { name: "Google Drive", icon: Cloud, desc: "Docs & sheets" },
    { name: "OneDrive / SharePoint", icon: HardDrive, desc: "Files & folders" },
  ];
  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" data-testid="connectors-modal">
      <div className="bg-[#0B0F17] border border-white/10 rounded-2xl w-full max-w-lg shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-2.5"><Plug size={18} className="text-emerald-400" /><span className="font-head font-bold text-white">Connected apps</span></div>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-2">
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <div key={it.name} className="flex items-center gap-3 px-3 py-3 rounded-xl bg-white/5 border border-white/10">
                <span className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-slate-400"><Icon size={17} /></span>
                <div className="flex-1">
                  <div className="text-sm text-slate-100">{it.name}</div>
                  <div className="text-[11px] text-slate-500">{it.desc}</div>
                </div>
                <span className="text-[11px] px-2 py-1 rounded-md bg-amber-500/10 text-amber-300 border border-amber-500/20">Not connected</span>
              </div>
            );
          })}
          <p className="text-[11px] text-slate-500 px-1 pt-1">
            Integrations require OAuth credentials for each provider. The architecture is in place; connections activate once credentials are configured on the server.
          </p>
        </div>
      </div>
    </div>
  );
}

function SchedulesModal({ open, onClose, dataset, dataFilters, dataLogic }) {
  const [data, setData] = React.useState(null);
  const [form, setForm] = React.useState({ name: "", format: "pdf", intervalMinutes: 60 });
  const [busy, setBusy] = React.useState(false);
  const load = React.useCallback(() => { api.listSchedules().then(setData).catch(() => {}); }, []);
  React.useEffect(() => { if (open) load(); }, [open, load]);
  if (!open) return null;

  const create = async () => {
    if (!dataset) { toast.error("Open a dataset first to schedule a report."); return; }
    try {
      setBusy(true);
      await api.createSchedule({ name: form.name || `Report for ${dataset.filename}`, datasetId: dataset.datasetId,
        filename: dataset.filename, format: form.format, filters: dataFilters, logic: dataLogic,
        intervalMinutes: Number(form.intervalMinutes) });
      setForm({ name: "", format: "pdf", intervalMinutes: 60 }); load();
      toast.success("Schedule created");
    } catch (e) { toast.error("Could not create schedule"); } finally { setBusy(false); }
  };
  const runNow = async (id) => {
    try {
      toast.loading("Running…", { id: "rn" });
      const r = await api.runScheduleNow(id);
      downloadBase64(r.report.base64, r.report.filename, r.report.mime);
      toast.success("Report generated", { id: "rn" }); load();
    } catch (e) { toast.error("Run failed", { id: "rn" }); }
  };
  const del = async (id) => { await api.deleteSchedule(id); load(); };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-start justify-center p-4 overflow-y-auto" data-testid="schedules-modal">
      <div className="bg-[#0B0F17] border border-white/10 rounded-2xl w-full max-w-2xl my-8 shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-2.5"><Clock size={18} className="text-emerald-400" /><span className="font-head font-bold text-white">Scheduled reports</span></div>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-4">
          {data && !data.emailConfigured && (
            <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
              Email delivery requires configuration (no email provider set). Schedules still run and generate reports you can download from the run history.
            </div>
          )}
          <div className="bg-[#111827] border border-white/10 rounded-xl p-4">
            <div className="text-sm font-semibold text-white mb-2">New schedule {dataset ? `for “${dataset.filename}”` : "(open a dataset first)"}</div>
            <div className="grid sm:grid-cols-4 gap-2">
              <input data-testid="schedule-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Report name" className="sm:col-span-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-200" />
              <select data-testid="schedule-format" value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })} className="bg-white/5 border border-white/10 rounded-lg px-2 py-2 text-sm text-slate-200">
                {["pdf", "xlsx", "docx", "pptx"].map((f) => <option key={f} value={f} className="bg-[#111827]">{f.toUpperCase()}</option>)}
              </select>
              <select data-testid="schedule-interval" value={form.intervalMinutes} onChange={(e) => setForm({ ...form, intervalMinutes: e.target.value })} className="bg-white/5 border border-white/10 rounded-lg px-2 py-2 text-sm text-slate-200">
                <option value={60} className="bg-[#111827]">Hourly</option>
                <option value={1440} className="bg-[#111827]">Daily</option>
                <option value={10080} className="bg-[#111827]">Weekly</option>
              </select>
            </div>
            <button data-testid="create-schedule-button" onClick={create} disabled={busy || !dataset} className="mt-2 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold disabled:opacity-40">Create schedule</button>
          </div>
          <div className="space-y-2">
            {(data?.schedules || []).length === 0 && <div className="text-xs text-slate-600">No schedules yet.</div>}
            {(data?.schedules || []).map((s) => (
              <div key={s.id} data-testid="schedule-item" className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-white/5 border border-white/10">
                <Clock size={15} className="text-emerald-400" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-100 truncate">{s.name} · {String(s.format).toUpperCase()} · every {s.intervalMinutes}m</div>
                  <div className="text-[11px] text-slate-500">{s.filename} · last run: {s.lastRun ? new Date(s.lastRun).toLocaleString() : "—"} {s.runs?.length ? `(${s.runs[s.runs.length - 1].status})` : ""}</div>
                </div>
                <button data-testid="run-schedule-now" onClick={() => runNow(s.id)} className="text-slate-400 hover:text-emerald-300" title="Run now"><Play size={15} /></button>
                <button onClick={() => del(s.id)} className="text-slate-400 hover:text-red-400" title="Delete"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <Root />
    </AuthProvider>
  );
}

function Root() {
  const { user, ready } = useAuth();
  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0B0F17]">
        <Loader2 className="animate-spin text-emerald-500" size={28} />
      </div>
    );
  }
  if (!user) return (<><Toaster theme="dark" position="top-center" richColors /><AuthScreen /></>);
  return <AppShell />;
}

function AppShell() {
  const { user, logout } = useAuth();
  const [config, setConfig] = useState(null);
  const [usage, setUsage] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataset, setDataset] = useState(null);
  const [datasets, setDatasets] = useState([]);
  const [dataFilters, setDataFilters] = useState([]);
  const [dataLogic, setDataLogic] = useState("AND");
  const [showData, setShowData] = useState(false);
  const [showResearch, setShowResearch] = useState(false);
  const [showConnectors, setShowConnectors] = useState(false);
  const [showSchedules, setShowSchedules] = useState(false);
  const [showDeveloper, setShowDeveloper] = useState(false);
  const [showAgent, setShowAgent] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [showSelfAI, setShowSelfAI] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeModule, setActiveModule] = useState("Universal AI");

  const refreshSessions = useCallback(async () => {
    try { setSessions(await api.listSessions()); } catch (e) {}
  }, []);

  useEffect(() => {
    (async () => {
      try { setConfig(await api.getConfig()); } catch (e) { toast.error("Backend unavailable"); }
      try { setUsage(await api.getUsage()); } catch (e) {}
      refreshSessions();
    })();
  }, [refreshSessions]);

  const newChat = () => { setActiveId(null); setMessages([]); setSidebarOpen(false); };

  const selectSession = async (id) => {
    try {
      const s = await api.getSession(id);
      setActiveId(id); setMessages(s.messages || []); setSidebarOpen(false);
    } catch (e) { toast.error("Could not load conversation"); }
  };

  const deleteSession = async (id) => {
    await api.deleteSession(id);
    if (activeId === id) newChat();
    refreshSessions();
  };

  const onDataset = (res) => {
    setDataset(res);
    setDataFilters([]); setDataLogic("AND");
    setDatasets((prev) => prev.some((d) => d.datasetId === res.datasetId) ? prev : [...prev, res]);
    setShowData(true);
  };

  const exportReport = async (msg, format) => {
    if (!dataset) { toast.error("No active dataset to export."); return; }
    try {
      toast.loading("Generating report…", { id: "exp" });
      const dr = msg?.dataResult;
      const res = await api.exportReport({
        datasetId: msg?.datasetId || dataset.datasetId, format,
        title: `SSG AI — ${dataset.filename}`, filters: dataFilters, logic: dataLogic,
        result: dr ? { columns: dr.resultColumns, rows: dr.resultRows, scalar: dr.scalar, narration: msg.content } : null,
      });
      downloadBase64(res.base64, res.filename, res.mime);
      toast.success(`Downloaded ${res.filename}`, { id: "exp" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Export failed", { id: "exp" }); }
  };

  const sendMessage = async (payload) => {
    const userMsg = {
      id: uid(), role: "user", content: payload.message || "",
      hasImage: !!payload.imageBase64, timestamp: new Date().toISOString(),
    };
    setMessages((m) => [...m, userMsg]);
    setLoading(true);
    try {
      const res = await api.chat({
        sessionId: activeId, message: payload.message || "Describe this.",
        imageBase64: payload.imageBase64, documentText: payload.documentText,
        datasetSummary: dataset?.summary, datasetId: dataset?.datasetId,
        module: payload.module,
      });
      if (!activeId) { setActiveId(res.sessionId); refreshSessions(); }
      setMessages((m) => [...m, res.message]);
      refreshSessions();
      api.getUsage().then(setUsage).catch(() => {});
      if (res.locked) {
        toast.message("You've reached your 10th free question. Subscribe to unlock this answer.", {
          action: { label: "Upgrade", onClick: () => setShowPricing(true) },
        });
      } else if (res.subscribePrompt) {
        toast.message("1 free question left today. Subscribe for unlimited access.", {
          action: { label: "See plans", onClick: () => setShowPricing(true) },
        });
      }
    } catch (e) {
      const raw = e?.response?.data?.detail || "";
      const CODES = {
        FREE_LIMIT_REACHED: "You've used all 10 free questions for today. Subscribe to Basic or Pro to continue — your free questions reset 24 hours after your first one.",
        SUBSCRIBE_TO_UNLOCK: "This is your 10th free question. Subscribe to Basic or Pro to unlock it.",
      };
      const isLimit = raw === "FREE_LIMIT_REACHED" || raw === "SUBSCRIBE_TO_UNLOCK";
      const detail = CODES[raw] || raw || "SSG AI is temporarily unable to process this request. Please try again.";
      if (isLimit) {
        toast.message(detail, { action: { label: "Upgrade", onClick: () => setShowPricing(true) } });
      } else {
        setMessages((m) => [...m, { id: uid(), role: "model", content: `⚠️ ${detail}`, isError: true, timestamp: new Date().toISOString() }]);
        toast.error(detail);
      }
    } finally { setLoading(false); }
  };

  const regenerate = (modelMsg) => {
    const idx = messages.findIndex((x) => x.id === modelMsg.id);
    let userText = "";
    for (let i = idx - 1; i >= 0; i--) { if (messages[i].role === "user") { userText = messages[i].content; break; } }
    if (!userText) { toast.message("Nothing to regenerate."); return; }
    sendMessage({ message: userText, module: activeModule });
  };

  return (
    <div className="App flex bg-[#0B0F17] text-slate-100">
      <Toaster theme="dark" position="top-center" richColors />
      <Sidebar
        open={sidebarOpen} onClose={() => setSidebarOpen(false)}
        sessions={sessions} activeId={activeId} onSelect={selectSession} onNew={newChat} onDelete={deleteSession}
        config={config} usage={usage} dataset={dataset}
        onOpenData={() => setShowData(true)} onOpenResearch={() => setShowResearch(true)} onOpenConnectors={() => setShowConnectors(true)}
        onOpenSelfAI={() => setShowSelfAI(true)}
        activeModule={activeModule} onModule={(m) => { setActiveModule(m); if (m === "Developer AI") setShowDeveloper(true); }}
      />

      <main className="flex-1 flex flex-col min-w-0 h-screen">
        {/* top bar */}
        <header className="flex items-center gap-3 px-4 py-3 border-b border-white/10 backdrop-blur-xl bg-[#0B0F17]/70 sticky top-0 z-30">
          <button className="lg:hidden text-slate-300" onClick={() => setSidebarOpen(true)} data-testid="open-sidebar-button"><Menu size={20} /></button>
          <Logo size={28} className="hidden lg:block" />
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-white">{activeModule}</span>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">IN</span>
          </div>
          <div className="flex-1" />
          {config && !config.ai?.configured && (
            <span className="text-[11px] text-amber-300 flex items-center gap-1"><Globe size={12} /> AI not configured</span>
          )}
          <button onClick={() => setShowAgent(true)} className="hidden sm:flex items-center gap-1.5 text-xs text-slate-400 hover:text-blue-300" data-testid="header-agent-button">
            <Bot size={14} /> Agent
          </button>
          <button onClick={() => setShowDeveloper(true)} className="hidden sm:flex items-center gap-1.5 text-xs text-slate-400 hover:text-emerald-300" data-testid="header-developer-button">
            <Code2 size={14} /> Developer
          </button>
          <button onClick={() => setShowConnectors(true)} className="hidden sm:flex items-center gap-1.5 text-xs text-slate-400 hover:text-white" data-testid="header-connectors-button">
            <Plug size={14} /> Apps
          </button>
          <button onClick={() => setShowSchedules(true)} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white" data-testid="header-schedules-button">
            <Clock size={14} /> Schedules
          </button>
          <button onClick={() => setShowSelfAI(true)} className="hidden sm:flex items-center gap-1.5 text-xs text-slate-400 hover:text-emerald-300" data-testid="header-selfai-button">
            <LifeBuoy size={14} /> Self AI
          </button>
          <button onClick={() => setShowPricing(true)} className="flex items-center gap-1.5 text-xs font-semibold text-emerald-300 hover:text-emerald-200 px-2.5 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20" data-testid="header-upgrade-button">
            <Sparkles size={13} /> Upgrade
          </button>
          <button onClick={() => setShowMembers(true)} className="flex items-center gap-1.5 text-xs text-slate-300 hover:text-white pl-2 border-l border-white/10" data-testid="header-team-button" title={`${user?.companyName} · ${user?.email}`}>
            <span className="w-7 h-7 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-300 text-xs font-bold">{(user?.name || user?.email || "?")[0]?.toUpperCase()}</span>
            <span className="hidden md:inline max-w-[120px] truncate">{user?.companyName}</span>
          </button>
          <button onClick={logout} className="text-slate-400 hover:text-red-400" data-testid="logout-button" title="Sign out"><LogOut size={16} /></button>
        </header>

        <MessageList messages={messages} loading={loading} onSuggest={(s) => sendMessage({ message: s, module: activeModule })} activeModule={activeModule} onExport={exportReport} onRegenerate={regenerate} onUpgrade={() => setShowPricing(true)} />

        <Composer
          onSend={sendMessage} onDataset={onDataset} config={config}
          disabled={loading || (config && !config.ai?.configured)} activeModule={activeModule}
        />
      </main>

      {showData && dataset && (
        <DataStudio
          dataset={dataset} onClose={() => setShowData(false)}
          datasets={datasets}
          onFiltersChange={(f, l) => { setDataFilters(f); setDataLogic(l); }}
          onJoined={onDataset}
        />
      )}
      <DeepResearch open={showResearch} onClose={() => setShowResearch(false)} aiConfigured={config?.ai?.configured} />
      <ConnectorsModal open={showConnectors} onClose={() => setShowConnectors(false)} />
      <SchedulesModal open={showSchedules} onClose={() => setShowSchedules(false)} dataset={dataset} dataFilters={dataFilters} dataLogic={dataLogic} />
      <DeveloperPanel open={showDeveloper} onClose={() => setShowDeveloper(false)} />
      <AgentPanel open={showAgent} onClose={() => setShowAgent(false)} dataset={dataset} />
      <MembersModal open={showMembers} onClose={() => setShowMembers(false)} usage={usage} onUsageReset={() => api.getUsage().then(setUsage).catch(() => {})} />
      <PricingModal open={showPricing} onClose={() => setShowPricing(false)} currentTier={usage?.tier} onActivated={() => { api.getUsage().then(setUsage).catch(() => {}); if (activeId) selectSession(activeId); }} />
      <SelfAIModal open={showSelfAI} onClose={() => setShowSelfAI(false)} />
    </div>
  );
}
